import React, { useContext, useEffect, useState } from 'react'
import { Box, Button, Grid, Typography, Alert, Link } from '@mui/material'
import FixedWidthContainer from 'components/FixedWidthContainer'
import { CartContext } from 'contexts/CartContext'
import { useNavigate } from 'react-router-dom'
import SectionHeader from 'components/SectionHeader'
import BatchEdit from 'components/BatchEdit'
import CartSummary from 'pages/Cart/CartSummary'
import CartItem from 'components/CartItem'
import { validateWoodenTableRequirements } from 'utils/validateWoodenTable'
import { useTranslation } from 'react-i18next'
import { syncProductsStockBySkus } from 'api/products'
import { useBatchColorEdit } from 'hooks/useBatchColorEdit'

const CartPage = () => {
  const { t } = useTranslation()
  const {
    cart,
    clearCartUi,
    updateCartItem,
    switchToQuote,
    deleteCartItem,
    clearCart,
    getCartDetails,
    discountCode,
    setDiscountCode,
    discountResult,
    validateDiscountCode,
    removeDiscount
  } = useContext(CartContext)

  const navigate = useNavigate()

  const [stocks, setStocks] = useState<{ [sku: string]: number }>({})

  useEffect(() => {
    if (!cart?.items?.length) return
    syncProductsStockBySkus(cart.items.map(item => item.sku)).then(products => {
      const stockMap = Object.fromEntries(
        products.map(p => [p.sku, Number(p.stock)])
      )
      setStocks(stockMap)
    })
  }, [cart?.items])

  const handleCreateQuote = async () => {
    switchToQuote()
    clearCartUi()
    navigate('/quotes')
  }

  const handleUpdateCartItem = async ({
    cartItemId,
    quantity,
    isAssembled,
    hingeDirection,
    skuPrefix,
    poNumber
  }: {
    cartItemId?: string
    quantity?: number
    isAssembled?: boolean
    hingeDirection?: string
    skuPrefix?: string
    poNumber?: string
  }) => {
    if (!cartItemId && poNumber !== undefined) {
      updateCartItem({ poNumber })
      return
    }

    const currentItem = cart.items.find(item => item.cartItemId === cartItemId)
    if (!currentItem) {
      console.error(`Item with cartItemId ${cartItemId} not found in cart`)
      return
    }

    const updatedIsAssembled =
      isAssembled !== undefined ? isAssembled : currentItem.isAssembled || false

    const updatedHingeDirection =
      updatedIsAssembled && currentItem.isHingeable
        ? hingeDirection || 'LEFT'
        : null

    updateCartItem({
      cartItemId,
      quantity,
      isAssembled: updatedIsAssembled,
      hingeDirection: updatedHingeDirection,
      skuPrefix,
      poNumber
    })
  }

  const { subTotal, discountedAmount, tax, assemblyFee, totalPrice } =
    getCartDetails()

  const {
    isBatchEditing,
    selectedItems,
    notFoundItems,
    outOfStockItems,
    isAllSelected,
    selectItem,
    toggleSelectAll,
    openBatchEdit,
    closeBatchEdit,
    runBatchUpdate
  } = useBatchColorEdit(cart?.items ?? [], cart?.id ?? '')

  const woodenTableValidation = validateWoodenTableRequirements(
    cart?.items || []
  )

  return (
    <FixedWidthContainer sx={{ my: '50px' }}>
      <SectionHeader title={t('cart.title')} />
      {cart?.items?.length ? (
        <Grid container spacing={3}>
          <Grid item xs={8}>
            <Box
              display='flex'
              justifyContent='flex-end'
              alignItems='center'
              sx={{ mb: 2 }}
            >
              <Box
                sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}
              >
                {isBatchEditing && (
                  <Button
                    variant={
                      isAllSelected
                        ? 'contained'
                        : ('outlined' as 'contained' | 'outlined')
                    }
                    sx={{
                      width: '150px',
                      height: '32px',
                      gap: '10px',
                      borderRadius: '4px',
                      borderColor: 'primary.main',
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '16.94px',
                      textAlign: 'left'
                    }}
                    onClick={toggleSelectAll}
                  >
                    {t('batchEdit.selectAll')}
                  </Button>
                )}
                <Button
                  variant={
                    isBatchEditing
                      ? 'contained'
                      : ('outlined' as 'contained' | 'outlined')
                  }
                  sx={{
                    width: '150px',
                    height: '32px',
                    gap: '10px',
                    borderRadius: '4px',
                    borderColor: 'primary.main',
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '16.94px',
                    textAlign: 'left'
                  }}
                  onClick={() =>
                    isBatchEditing ? closeBatchEdit() : openBatchEdit()
                  }
                >
                  {t('cart.batchEdit')}
                </Button>

                <Button
                  sx={{
                    width: '150px',
                    height: '32px',
                    gap: '10px',
                    borderRadius: '4px',
                    color: 'primary.main',
                    border: '1px solid',
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '16.94px',
                    textAlign: 'left'
                  }}
                  onClick={clearCart}
                >
                  {t('cart.clearBag')}
                </Button>
              </Box>
            </Box>

            {isBatchEditing && (
              <BatchEdit
                selectedItems={selectedItems}
                notFoundItems={notFoundItems}
                outOfStockItems={outOfStockItems}
                onBatchUpdate={runBatchUpdate}
                onCancel={closeBatchEdit}
              />
            )}

            {cart.items.map(item => (
              <CartItem
                key={item.cartItemId}
                item={item}
                stock={stocks[item.sku]}
                isSelected={selectedItems.includes(item.cartItemId)}
                isBatchEditActive={isBatchEditing}
                onSelect={isSelected => selectItem(item.cartItemId, isSelected)}
                onUpdate={updateParams =>
                  handleUpdateCartItem({
                    cartItemId: item.cartItemId,
                    ...updateParams
                  })
                }
                onDelete={() => deleteCartItem(item.cartItemId)}
              />
            ))}

            {woodenTableValidation.warning && (
              <Alert severity='error' sx={{ mt: 2 }}>
                {woodenTableValidation.messageParts ? (
                  <>
                    {woodenTableValidation.messageParts.beforeLink}
                    <Link
                      href='/search?keyword=-ber'
                      sx={{
                        color: 'error.main',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                    >
                      {woodenTableValidation.messageParts.linkText}
                    </Link>
                    {woodenTableValidation.messageParts.afterLink}
                  </>
                ) : (
                  woodenTableValidation.warning
                )}
              </Alert>
            )}
          </Grid>

          <Grid item xs={4} container justifyContent='flex-end'>
            <CartSummary
              subTotal={subTotal}
              discountedAmount={discountedAmount}
              tax={tax}
              assemblyFee={assemblyFee}
              totalPrice={totalPrice}
              discountResult={discountResult}
              validateDiscountCode={validateDiscountCode}
              removeDiscount={removeDiscount}
              discountCode={discountCode}
              setDiscountCode={setDiscountCode}
              handleCreateQuote={handleCreateQuote}
              handleCheckout={() => navigate('/checkout')}
              poNumber={cart.poNumber || ''}
              onUpdatePoNumber={(newPoNumber: string) =>
                handleUpdateCartItem({ poNumber: newPoNumber })
              }
              isCheckoutDisabled={!woodenTableValidation.isValid}
            />
          </Grid>
        </Grid>
      ) : (
        <Box display='flex' justifyContent='center' alignItems='center'>
          <Typography variant='h4' color='text.secondary' gutterBottom>
            {t('cart.emptyMessage')}
          </Typography>
        </Box>
      )}
    </FixedWidthContainer>
  )
}

export default CartPage
